import React from "react";
import { BadgeCheck, Check, Eye, Settings2, Upload } from "lucide-react";
import { useTranslation } from "react-i18next";

const STEPS = [
  {
    id: 1,
    titleKey: "certificateOnboarding:step_upload_title",
    Icon: Upload,
  },
  {
    id: 2,
    titleKey: "certificateOnboarding:step_configure_title",
    Icon: Settings2,
  },
  {
    id: 3,
    titleKey: "certificateOnboarding:step_preview_title",
    Icon: Eye,
  },
  {
    id: 4,
    titleKey: "certificateOnboarding:step_complete_title",
    Icon: BadgeCheck,
  },
];

const CIRCLE_SIZE_CLASS = "w-14 h-14";

function getStepStatus(stepId, currentStep) {
  if (currentStep > stepId) return "completed";
  if (currentStep === stepId) return "active";
  return "upcoming";
}

function StepCircle({ step, status }) {
  const { Icon } = step;
  const isHighlighted = status === "active" || status === "completed";

  return (
    <div
      className={`relative flex items-center justify-center shrink-0 ${CIRCLE_SIZE_CLASS} rounded-full shadow-sm transition-all duration-300 ${
        isHighlighted
          ? "bg-gradient-to-br from-cfPrimaryButton via-cfPrimary to-cfPrimaryLight text-cfSecondary"
          : "bg-cfInactiveSurface text-cfTextBreadcrumbDisabled"
      }`}
    >
      {status === "completed" ? (
        <Check className="w-6 h-6" strokeWidth={2.5} />
      ) : (
        <Icon className="w-6 h-6" strokeWidth={1.75} />
      )}
    </div>
  );
}

function StepConnector({ isComplete }) {
  return (
    <div
      className="flex-1 flex items-center min-w-[1.5rem] sm:min-w-[2.5rem]"
      aria-hidden="true"
    >
      <div className="relative w-full h-1 rounded-full bg-cfInactiveSurface overflow-hidden">
        <div
          className={`absolute inset-y-0 left-0 rounded-full transition-all duration-500 ease-out ${
            isComplete
              ? "w-full bg-gradient-to-r from-cfPrimaryButton via-cfPrimary to-cfPrimaryLight"
              : "w-0 bg-cfPrimary"
          }`}
        />
      </div>
    </div>
  );
}

function CertificateOnboardingStepper({ currentStep, steps = STEPS }) {
  const { t } = useTranslation();

  return (
    <div
      id="cert-onboarding-stepper"
      className="w-full py-4 sm:py-6"
      role="list"
      aria-label={t("certificateOnboarding:stepper_aria_label")}
    >
      <div className="w-full max-w-xl sm:max-w-3xl mx-auto px-2 sm:px-4">
        {/* Circles + connectors only — keeps the line vertically centered on icons */}
        <div className="flex items-center w-full">
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <div
                className="shrink-0 flex justify-center w-14 sm:w-16"
                data-step-id={step.id}
                data-step-status={getStepStatus(step.id, currentStep)}
              >
                <StepCircle
                  step={step}
                  status={getStepStatus(step.id, currentStep)}
                />
              </div>
              {index < steps.length - 1 && (
                <StepConnector isComplete={currentStep > step.id} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Labels on a separate row — same column widths as circles row */}
        <div className="flex w-full mt-3 sm:mt-4">
          {steps.map((step, index) => {
            const status = getStepStatus(step.id, currentStep);
            const isHighlighted = status === "active" || status === "completed";

            return (
              <React.Fragment key={`label-${step.id}`}>
                <div className="shrink-0 w-14 sm:w-16 flex justify-center overflow-visible">
                  <p
                    className={`text-center text-xs sm:text-sm font-medium whitespace-nowrap transition-colors duration-300 ${
                      isHighlighted
                        ? "text-cfHeadingText"
                        : "text-cfTextBreadcrumbDisabled"
                    }`}
                  >
                    {t(step.titleKey)}
                  </p>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className="flex-1 min-w-[1.5rem] sm:min-w-[2.5rem]"
                    aria-hidden="true"
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default CertificateOnboardingStepper;
